import { noop } from "lodash";
import { PropsWithChildren, useEffect } from "react";
import { Route, RouteProps, Switch, useLocation } from "react-router-dom";
import { Redirect } from "react-router-dom";
import { CreatedArtifactFragmentFragment } from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import Account from "@apps/account/account";
import AgentPage from "@apps/agent/agent-page";
import ArtifactCreationDialog from "@apps/artifact-creation-dialog/artifact-creation-dialog";
import ArtifactFullscreen from "@apps/artifact-fullscreen/artifact-fullscreen";
import ArtifactSidebarRoute from "@apps/artifact-sidebar/artifact-sidebar-route";
import AssessmentSidebarRoute from "@apps/assessments/components/assessment/assessment-sidebar-route";
import AssessmentDeliverySidebarRoute from "@apps/assessments/components/delivery/assessment-delivery-sidebar-route";
import Assessments from "@apps/assessments/page";
import ConnectCalendarModal from "@apps/connect-calendar-modal/connect-calendar-modal";
import DashboardNew from "@apps/dashboard-new/dashboard";
import RedirectDashboardToProfile from "@apps/dashboard-new/redirect-dashboard-to-profile";
import EngagementSurvey from "@apps/engagement-survey/page";
import Error404 from "@apps/error-404";
import ExplorerPage from "@apps/explorer/page";
import FeedbackPage from "@apps/feedback/feedback";
import GoalAlignment from "@apps/goal-alignment/goal-alignment";
import GoalOverview from "@apps/goal-overview/page";
import GoalPage from "@apps/goal-page/page";
import GoogleCalSetup from "@apps/google-cal-setup";
import Home from "@apps/home/home";
import InviteDialog from "@apps/invite-dialog/invite-dialog";
import KPIs from "@apps/kpis/page";
import MeetingDialog from "@apps/meeting-dialog/meeting-dialog";
import MeetingGroupRoutes from "@apps/meeting-new/meeting-group-routes";
import MeetingRoutes from "@apps/meeting-new/meeting-routes";
import MeetingsRoutes from "@apps/meeting-new/meetings-routes";
import MeetingSettings from "@apps/meeting-settings/settings";
import MeetingSidebar from "@apps/meeting-sidebar/meeting-sidebar";
import OrgChart from "@apps/org-chart/org-chart";
import OrgSettings from "@apps/org-settings/org-settings";
import ProfilePage from "@apps/profile/profile-page";
import ProfileSidebar from "@apps/profile/profile-sidebar";
import Programs from "@apps/programs/page";
import Ratings from "@apps/ratings/page";
import RecognitionBoard from "@apps/recognition-board/recognition-board";
import Reporting from "@apps/reporting/page";
import StyleGuide from "@apps/styleguide/styleguide";
import Templates from "@apps/templates/page";
import WorkHistory from "@apps/work-history/page";
import { currentOrganizationVar, currentUserVar } from "@cache/cache";
import BillingDialog from "@components/billing-dialog-cta/billing-dialog";
import { useLink } from "@components/link/link-helpers";
import { artifactTypeUrl } from "@helpers/constants";
import { getUrl, toWithBackground } from "@helpers/helpers";

import Layout from "./components/layout";
import { artifactTypePaths, meetingsRoutes } from "./routes-constants";

const CustomRouteWithLayout: React.FC<PropsWithChildren<RouteProps>> = ({
  children,
  ...props
}) => {
  return (
    <Route {...props}>
      <Layout>{children}</Layout>
    </Route>
  );
};

const CustomRoute: React.FC<PropsWithChildren<RouteProps>> = ({
  children,
  ...props
}) => {
  return (
    <Route {...props}>
      <Layout>{children}</Layout>
    </Route>
  );
};

const Routes: React.FC = () => {
  const currentUser = currentUserVar();
  const link = useLink();
  const currentOrganization = currentOrganizationVar();
  const location = useLocation<TFLocationState>();
  const background = location.state?.background;

  const handleRedirectHomepage = () => {
    link.redirect("/");
  };

  useEffect(() => {
    if (
      !window.Cypress &&
      !import.meta.env.DEV &&
      !window.TF.hijacked &&
      window.Intercom
    ) {
      window.Intercom("update");
    }
  }, [location]);

  // Implemented for the product tour in Intercom
  // we need to redirect to /connect-calendar when
  // the user clicks anchor tag #connect-calendar
  // This prevents a full page refresh
  useEffect(() => {
    if (location.hash === "#connect-calendar") {
      link.replace(
        toWithBackground({
          pathname: "/connect-calendar",
          location,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  const handleCloseArtifactCreationDialog = (
    artifact?: CreatedArtifactFragmentFragment,
  ) => {
    if (artifact) {
      link.redirect(
        toWithBackground({
          pathname: getUrl({
            artifactId: artifact.id,
            artifactType: artifact.artifactType,
          }),
          location,
        }),
      );
    } else {
      link.redirect("/");
    }
  };

  return (
    <>
      {/* Pages */}
      <Switch location={background || location}>
        <CustomRouteWithLayout exact path={["/", "/dashboard"]}>
          <Home />
        </CustomRouteWithLayout>
        <CustomRouteWithLayout
          exact
          path={["/ask-ai/:chatSessionId", "/ask-ai/"]}
        >
          <Home />
        </CustomRouteWithLayout>
        <CustomRouteWithLayout path="/dashboard/user/:userId">
          <RedirectDashboardToProfile />
        </CustomRouteWithLayout>
        <CustomRouteWithLayout path="/recognition-board">
          <RecognitionBoard />
        </CustomRouteWithLayout>
        <CustomRouteWithLayout path="/feedbacks">
          <FeedbackPage />
        </CustomRouteWithLayout>
        <CustomRouteWithLayout path="/explorer">
          <ExplorerPage />
        </CustomRouteWithLayout>
        <CustomRouteWithLayout path="/templates">
          <Templates />
        </CustomRouteWithLayout>
        <CustomRouteWithLayout path="/assessments">
          <Assessments />
        </CustomRouteWithLayout>
        <CustomRouteWithLayout path="/programs">
          <Programs />
        </CustomRouteWithLayout>
        <CustomRouteWithLayout path="/work-history">
          <WorkHistory />
        </CustomRouteWithLayout>
        <CustomRouteWithLayout path="/reporting">
          <Reporting />
        </CustomRouteWithLayout>
        <CustomRouteWithLayout path="/ratings">
          <Ratings />
        </CustomRouteWithLayout>
        <CustomRouteWithLayout path="/kpis">
          <KPIs />
        </CustomRouteWithLayout>
        <CustomRouteWithLayout path={["/goals/:type/:id", "/goals"]}>
          <GoalOverview />
        </CustomRouteWithLayout>
        <CustomRouteWithLayout path="/goal-alignment">
          <GoalAlignment />
        </CustomRouteWithLayout>
        <CustomRouteWithLayout path="/account">
          <Account />
        </CustomRouteWithLayout>
        <CustomRouteWithLayout path="/agent">
          <AgentPage />
        </CustomRouteWithLayout>
        <CustomRouteWithLayout path="/org-chart">
          <OrgChart />
        </CustomRouteWithLayout>
        <CustomRouteWithLayout path="/survey">
          <EngagementSurvey />
        </CustomRouteWithLayout>
        <CustomRouteWithLayout path="/google-cal-setup">
          <GoogleCalSetup />
        </CustomRouteWithLayout>
        <CustomRouteWithLayout
          path={[
            "/settings/organization/:organizationId",
            "/settings/organization",
          ]}
        >
          <OrgSettings />
        </CustomRouteWithLayout>
        <CustomRouteWithLayout path="/invite">
          <DashboardNew />
          <InviteDialog organization={currentOrganization} />
        </CustomRouteWithLayout>
        <CustomRouteWithLayout path="/connect-calendar">
          <DashboardNew />
          <ConnectCalendarModal open={true} onClose={handleRedirectHomepage} />
        </CustomRouteWithLayout>
        <CustomRouteWithLayout path="/billing">
          <DashboardNew />
          <BillingDialog
            onClose={() => {
              link.redirect("/");
            }}
            organization={currentOrganization}
          />
        </CustomRouteWithLayout>

        <CustomRouteWithLayout path="/new-meeting">
          <MeetingDialog onClose={noop} />
        </CustomRouteWithLayout>

        {/* MEETING GROUP, MEETING & TOPIC */}
        <CustomRouteWithLayout path="/meeting/:meetingGroupId/settings">
          <MeetingSettings />
        </CustomRouteWithLayout>
        <CustomRouteWithLayout path={"/meeting/:meetingGroupId/:meetingId"}>
          <MeetingRoutes />
        </CustomRouteWithLayout>
        <CustomRouteWithLayout exact path={"/meeting/:meetingGroupId"}>
          <MeetingGroupRoutes />
        </CustomRouteWithLayout>
        <CustomRouteWithLayout path={meetingsRoutes}>
          <MeetingsRoutes />
        </CustomRouteWithLayout>

        <CustomRouteWithLayout path="/styleguide">
          <StyleGuide />
        </CustomRouteWithLayout>

        <CustomRouteWithLayout path="/profile/:profileId">
          <ProfilePage />
        </CustomRouteWithLayout>
        <CustomRouteWithLayout
          path={Object.values(artifactTypeUrl).map((path) => `/${path}-new`)}
        >
          <DashboardNew />
          <ArtifactCreationDialog onClose={handleCloseArtifactCreationDialog} />
        </CustomRouteWithLayout>
        <CustomRouteWithLayout
          exact
          path={`/(${artifactTypePaths})/:artifactId/fullscreen`}
        >
          <ArtifactFullscreen />
        </CustomRouteWithLayout>
        {/* Goals navigated to without a background location (Goals section
            links, deep links, the drawer's expand button) render as their own
            full page. Links carrying a background still open the drawer via
            the sidebar overlay block below. Must precede the generic artifact
            route so /goal/:id matches here first. */}
        <CustomRouteWithLayout exact path="/goal/:artifactId">
          <GoalPage />
        </CustomRouteWithLayout>
        <CustomRouteWithLayout path={`/(${artifactTypePaths})/:artifactId`}>
          <DashboardNew />
          <ArtifactSidebarRoute />
        </CustomRouteWithLayout>

        {/* login/register links */}
        <CustomRouteWithLayout path={["/login", "/register"]}>
          <Redirect to={`/dashboard/user/${currentUser.id}`} />
        </CustomRouteWithLayout>
        {/* Not found */}
        <CustomRouteWithLayout path="*">
          <Error404 />
        </CustomRouteWithLayout>
      </Switch>

      {/* Sidebars */}
      {background && (
        <>
          <CustomRoute
            path={Object.values(artifactTypeUrl).map((path) => `/${path}-new`)}
          >
            <ArtifactCreationDialog
              onClose={handleCloseArtifactCreationDialog}
            />
          </CustomRoute>
          <CustomRoute path="/connect-calendar">
            <ConnectCalendarModal
              open={true}
              onClose={handleRedirectHomepage}
            />
          </CustomRoute>

          <CustomRoute path="/invite">
            <InviteDialog organization={currentOrganization} />
          </CustomRoute>

          <CustomRoute
            exact
            path={[
              `/(${artifactTypePaths})/:artifactId`,
              `/meeting/:meetingGroupId/:meetingId/(${artifactTypePaths})/:artifactId`,
            ]}
          >
            <ArtifactSidebarRoute />
          </CustomRoute>
          <CustomRoute path={["/profile/:profileId"]}>
            <ProfileSidebar />
          </CustomRoute>
          <CustomRoute exact path={["/meeting/:meetingGroupId/:meetingId"]}>
            <MeetingSidebar />
          </CustomRoute>
          <CustomRoute
            exact
            path={"/assessments/assessment/delivery/:deliveryId"}
          >
            <AssessmentDeliverySidebarRoute />
          </CustomRoute>
          <CustomRoute exact path={"/assessments/assessment/:assessmentId"}>
            <AssessmentSidebarRoute />
          </CustomRoute>
        </>
      )}
    </>
  );
};

export default Routes;
